import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  Button,
  Input,
  Typography,
  Space,
  Table,
  message,
  Modal,
  DatePicker,
  Select,
  Form,
  Popconfirm,
  Tooltip
} from 'antd'
import {
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import moment from 'moment'
import { rules } from 'src/utils/rules'

const { Title, Text, Paragraph } = Typography

export const ApiKeyManagement = ({ token }) => {
  const dispatch = useDispatch()
  const {
    data: apiKeys,
    loading,
    meta
  } = useSelector((state) => state.reducerApiKeys)
  const [form] = Form.useForm()
  const [showApiKey, setShowApiKey] = useState({})
  const [newKeyModal, setNewKeyModal] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const calculateExpiryDate = (type) => {
    switch (type) {
      case '30days':
        return moment().add(30, 'days').format('YYYY-MM-DD')
      case '90days':
        return moment().add(90, 'days').format('YYYY-MM-DD')
      case '1year':
        return moment().add(1, 'year').format('YYYY-MM-DD')
      case 'never':
        return null
      case 'custom': {
        const customDate = form.getFieldValue('expiryDate')
        return customDate ? customDate.format('YYYY-MM-DD') : null
      }
      default:
        return null
    }
  }

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false
    return moment(expiresAt).isBefore(moment())
  }

  useEffect(() => {
    if (token) {
      dispatch({
        type: 'api_keys/get_api_keys',
        payload: { token }
      })
    }
  }, [dispatch, token])

  const handlePaginate = (page) => {
    dispatch({
      type: 'api_keys/get_api_keys',
      payload: { token, params: { page: page, name: searchValue } }
    })
  }

  const handleSearch = () => {
    dispatch({
      type: 'api_keys/get_api_keys',
      payload: { token, params: { name: searchValue } }
    })
  }

  const handleGenerateApiKey = async () => {
    try {
      const values = await form.validateFields()

      const expiresAt = calculateExpiryDate(values.expiryType)
      const apiKeyData = {
        name: values.name,
        expiresAt
      }

      dispatch({
        type: 'api_keys/create_api_key',
        payload: { token, data: apiKeyData }
      })

      setNewKeyModal(false)
      form.resetFields()
    } catch (error) {
      console.log('Validation failed:', error)
    }
  }

  const handleCopyApiKey = (key) => {
    navigator.clipboard.writeText(key)
    message.success('API Key berhasil disalin')
  }

  const handleToggleVisibility = (id) => {
    setShowApiKey((prev) => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const handleDeleteApiKey = (id) => {
    dispatch({
      type: 'api_keys/delete_api_key',
      payload: { token, apiKeyId: id }
    })
  }

  const maskApiKey = (key) => {
    if (!key || typeof key !== 'string') return '••••••••••••••••'
    if (key.length < 16) return '•'.repeat(key.length)

    return key.substring(0, 9) + '•••••' + key.substring(key.length - 5)
  }

  const apiKeyColumns = [
    {
      title: 'Nama',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: 'API Key',
      dataIndex: 'key',
      key: 'key',
      width: 220,
      render: (key, record) => (
        <Space>
          <Text code style={{ minWidth: '160px' }}>
            {showApiKey[record.id] ? key : maskApiKey(key)}
          </Text>
          <Button
            type='text'
            size='small'
            onClick={() => handleToggleVisibility(record.id)}
            title={showApiKey[record.id] ? 'Sembunyikan' : 'Tampilkan'}
          >
            {showApiKey[record.id] ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </Button>
        </Space>
      )
    },
    {
      title: 'Dibuat',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120
    },
    {
      title: 'Terakhir Digunakan',
      dataIndex: 'lastUsed',
      key: 'lastUsed',
      width: 160,
      render: (lastUsed) => (lastUsed ? moment(lastUsed).fromNow() : '-')
    },
    {
      title: 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 120,
      render: (expiresAt) => {
        if (!expiresAt) {
          return <Text type='secondary'>Never</Text>
        }

        const expired = isExpired(expiresAt)
        return (
          <Space>
            <Text style={{ color: expired ? '#ff4d4f' : 'inherit' }}>
              {expiresAt}
            </Text>
            {expired && (
              <Tooltip
                title={`API Key sudah expired ${moment(expiresAt).fromNow()}`}
              >
                <InfoCircleOutlined style={{ color: '#ff4d4f' }} />
              </Tooltip>
            )}
          </Space>
        )
      }
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type='text'
            size='small'
            onClick={() => handleCopyApiKey(record.key)}
            title='Salin API Key'
            className='button-detail'
          >
            <CopyOutlined />
          </Button>
          <Popconfirm
            title='Hapus API Key'
            description={`Apakah Anda yakin ingin menghapus API Key "${record.name}"?`}
            okText='Ya, Hapus'
            cancelText='Batal'
            okType='danger'
            onConfirm={() => handleDeleteApiKey(record.id)}
          >
            <Button
              type='text'
              size='small'
              title='Hapus API Key'
              className='button-delete'
            >
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <>
      <Card>
        <Space direction='vertical' style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Title level={4}>Kelola API Keys</Title>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => setNewKeyModal(true)}
              loading={loading}
            >
              Generate API Key
            </Button>
          </div>

          <Paragraph>
            API Keys digunakan untuk mengautentikasi permintaan API dari
            aplikasi third-party. Simpan API key Anda dengan aman dan jangan
            bagikan kepada pihak yang tidak berwenang.
          </Paragraph>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginBottom: '16px'
            }}
          >
            <Input
              placeholder='Cari API Key berdasarkan nama...'
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: '300px' }}
            />
            <Button
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              Cari
            </Button>
          </div>

          <Table
            columns={apiKeyColumns}
            dataSource={apiKeys}
            rowKey='id'
            scroll={{ x: 1040 }}
            pagination={
              meta && {
                defaultPageSize: meta.itemsPerPage,
                total: meta.totalItems,
                pageSize: meta.itemsPerPage,
                onChange: (page) => handlePaginate(page)
              }
            }
          />
        </Space>
      </Card>

      <Modal
        title='Generate API Key Baru'
        visible={newKeyModal}
        onOk={handleGenerateApiKey}
        onCancel={() => {
          setNewKeyModal(false)
          form.resetFields()
        }}
        okText='Generate'
        cancelText='Batal'
        confirmLoading={loading}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            label='Nama untuk API Key'
            name='name'
            rules={[rules.required()]}
          >
            <Input placeholder='Masukkan nama API Key (contoh: Production, Development)' />
          </Form.Item>

          <Form.Item
            label='Masa Berlaku'
            name='expiryType'
            rules={[rules.required()]}
          >
            <Select placeholder='Pilih masa berlaku'>
              <Select.Option value='30days'>30 Hari</Select.Option>
              <Select.Option value='90days'>90 Hari</Select.Option>
              <Select.Option value='1year'>1 Tahun</Select.Option>
              <Select.Option value='never'>Tidak Pernah Expired</Select.Option>
              <Select.Option value='custom'>Custom Date</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.expiryType !== currentValues.expiryType}
          >
            {({ getFieldValue }) =>
              getFieldValue('expiryType') === 'custom'
                ? (
                  <Form.Item
                    label='Tanggal Expired'
                    name='expiryDate'
                    rules={[rules.required()]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder='Pilih tanggal expired'
                      disabledDate={(current) => current && current < moment()}
                    />
                  </Form.Item>
                  )
                : null}
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

ApiKeyManagement.propTypes = {
  token: PropTypes.string
}
